import React from 'react';
import { useTranslation, i18n } from '@tasama/i18n';
import { cn, Card, OnboardingHeader, RadioGroup } from '@tasama/ui';

// RadioOption type definition (from UI package but not exported)
type RadioOption = {
  label: React.ReactNode;
  value: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  testID?: string | undefined;
  content?: React.ReactNode;
};

/**
 * Company Status Option Interface
 * Extends RadioOption with additional properties for the company status selection
 */
export interface CompanyStatusOption extends RadioOption {
  /** Whether this option shows the before you begin card */
  showBeforeYouBegin?: boolean;
}

/**
 * Company Status Screen Component Props
 * Based on Figma design: https://www.figma.com/design/IcSV9qhrjLJh6Y3Q8F7lcr/Tasama-UI-UX-Design?node-id=1764-45995
 */
export interface CompanyTypeScreenProps {
  /** Title for the onboarding header */
  title?: string;
  /** Description for the onboarding header */
  description?: string;
  /** Icon for the onboarding header */
  headerIcon?: React.ReactNode;
  /** Title size for the onboarding header */
  titleSize?: 'sm' | 'lg';
  /** Radio group options for company status */
  companyOptions?: CompanyStatusOption[];
  /** Default selected company status value */
  handleStatusChange: (_value: string) => void;
  selectedStatus?: string;
  /** Callback when company status changes */
  onStatusChange?: (_value: string) => void;
  /** Before you begin card configuration */
  beforeYouBeginCard?:
    | {
        title: string;
        description: string;
        items: Array<{ text: string; checked?: boolean }>;
      }
    | undefined;
  /** Navigation button configuration */
  navigationButtons?: {
    /** Show back button */
    showBack?: boolean;
    /** Back button text */
    backText?: string;
    /** Back button click handler */
    onBack?: () => void;
    /** Show continue button */
    showContinue?: boolean;
    /** Continue button text */
    continueText?: string;
    /** Continue button click handler */
    onContinue?: () => void;
    /** Continue button disabled state */
    continueDisabled?: boolean;
  };
  /** Additional CSS classes */
  className?: string;
  /** Test ID for QA automation */
  testID?: string | undefined;
  /** Radio group name for the input elements */
  radioGroupName?: string;
}

/**
 * Company Status Screen Component
 *
 * This screen component combines:
 * - OnboardingHeader for the main title and description
 * - RadioGroup for company status selection
 * - Card for displaying required documents when applicable
 *
 * Features:
 * - Follows Atomic Design principles (Screen level component)
 * - Reuses existing UI components (OnboardingHeader, RadioGroup, Card)
 * - Responsive design with proper spacing matching Figma design
 * - Accessibility support with proper ARIA attributes
 * - Error boundary for component-level error handling
 * - React 19 compatible (no forwardRef)
 * - Internal state management for selection
 * - Conditional rendering of "Before you begin" card based on selection
 */
class CompanyTypeScreenErrorBoundary extends React.Component<
  { children: React.ReactNode; errorMessage?: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; errorMessage?: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo) {
    // Log error for monitoring (wire logger if needed)
    // CompanyTypeScreen Error occurred
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div
          className="p-4 border border-red-200 rounded-lg bg-red-50"
          role="alert"
        >
          <p className="text-red-800 text-sm">
            {this.props.errorMessage ||
              i18n.t('companyType.errorBoundary', { ns: 'onboarding' })}
          </p>
        </div>
      );
    }
    return this.props.children as React.ReactElement;
  }
}

export const CompanyTypeScreen: React.FC<CompanyTypeScreenProps> = ({
  title,
  description,
  titleSize = 'lg',
  companyOptions = [],
  beforeYouBeginCard,
  className,
  testID = 'company-status-screen',
  radioGroupName = 'company-status',
  handleStatusChange,
  selectedStatus,
  headerIcon: _headerIcon,
  ...props
}) => {
  const { t } = useTranslation('onboarding');

  // Note: Individual option rendering handles conditional card display
  const translatedTitle = title ?? t('companyType.title');
  const translatedDescription = description ?? t('companyType.description');

  return (
    <CompanyTypeScreenErrorBoundary
      errorMessage={t('companyType.errorBoundary')}
    >
      <div
        className={cn(
          // Main container with max width and responsive design matching Figma
          'w-full mx-auto',
          className
        )}
        data-testid={testID}
        {...props}
      >
        {/* Onboarding Header Section */}
        <OnboardingHeader
          size={titleSize}
          title={translatedTitle}
          description={translatedDescription}
          icon={'Building2'}
          testID={`${testID}-header`}
        />

        {/* Company Status Selection Container */}
        <div
          className="flex flex-col gap-4 md:gap-6"
          data-testid={`${testID}-content`}
        >
          <RadioGroup
            name={radioGroupName}
            options={companyOptions.map((option) => ({
              ...option,
              content:
                option.showBeforeYouBegin && beforeYouBeginCard ? (
                  <Card
                    title={beforeYouBeginCard.title || ''}
                    description={beforeYouBeginCard.description || ''}
                    items={beforeYouBeginCard.items || []}
                    testID={`${testID}-before-you-begin-card`}
                  />
                ) : undefined,
            }))}
            value={selectedStatus || ''}
            onChange={handleStatusChange}
            className="flex flex-col gap-4 md:gap-6"
            optionContainerClassName="flex flex-col gap-4 w-full"
            itemClassName={cn(
              'gap-4 md:gap-5 rounded-2xl md:rounded-3xl p-4 md:p-8 border-brand-tint-300',
              // Mobile floor height so cards line up regardless of how many
              // lines the title wraps to. Tablet+ removes the constraint
              // because the wider column lets every title fit on one line.
              'min-h-[144px] md:min-h-0',
              'bg-background/90 transition-all duration-200',
              'hover:shadow-md'
            )}
            testID={`${testID}-radio-group`}
          />
        </div>
      </div>
    </CompanyTypeScreenErrorBoundary>
  );
};

CompanyTypeScreen.displayName = 'CompanyTypeScreen';

export default CompanyTypeScreen;
